import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Shield, AlertTriangle, AlertCircle, CheckCircle, XCircle, Clock, FileText, Download, Settings, Brain, Eye, Lock, Database, Users, Activity } from 'lucide-react';
import { AppBadge } from '../components/base/AppBadge';
import { PeriodSelector } from '../components/PeriodSelector';
type AIActTab = 'services' | 'requirements' | 'timeline';
interface ServiceCompliance {
  id: string;
  name: string;
  riskCategory: 'unacceptable' | 'high' | 'limited' | 'minimal';
  complianceScore: number;
  openIssues: number;
  lastAssessment: string;
  provider: string;
}
interface ComplianceRequirement {
  id: string;
  title: string;
  description: string;
  status: 'compliant' | 'in-progress' | 'non-compliant';
  category: 'risk' | 'data' | 'documentation' | 'oversight' | 'transparency' | 'security';
}
interface TimelineEvent {
  id: string;
  date: string;
  title: string;
  type: 'deadline' | 'milestone' | 'assessment';
  status: 'completed' | 'upcoming' | 'overdue';
}
const SERVICE_DATA: ServiceCompliance[] = [{
  id: 's1',
  name: 'Customer Assistant',
  riskCategory: 'limited',
  complianceScore: 92,
  openIssues: 1,
  lastAssessment: '2024-03-15',
  provider: 'OpenAI'
}, {
  id: 's2',
  name: 'Smart Matcher',
  riskCategory: 'high',
  complianceScore: 78,
  openIssues: 3,
  lastAssessment: '2024-03-10',
  provider: 'Anthropic'
}, {
  id: 's3',
  name: 'Content Moderator',
  riskCategory: 'high',
  complianceScore: 85,
  openIssues: 2,
  lastAssessment: '2024-03-12',
  provider: 'OpenAI'
}, {
  id: 's4',
  name: 'AI Tutor',
  riskCategory: 'limited',
  complianceScore: 95,
  openIssues: 0,
  lastAssessment: '2024-03-18',
  provider: 'Google Gemini'
}];
const REQUIREMENTS_DATA: ComplianceRequirement[] = [{
  id: 'r1',
  title: 'Risk Management System',
  description: 'Sistema di gestione del rischio implementato e documentato',
  status: 'compliant',
  category: 'risk'
}, {
  id: 'r2',
  title: 'Data Governance',
  description: 'Qualità dei dati e governance conforme ai requisiti',
  status: 'compliant',
  category: 'data'
}, {
  id: 'r3',
  title: 'Technical Documentation',
  description: 'Documentazione tecnica completa del sistema',
  status: 'in-progress',
  category: 'documentation'
}, {
  id: 'r4',
  title: 'Record Keeping & Logging',
  description: 'Logging dettagliato delle decisioni AI',
  status: 'non-compliant',
  category: 'documentation'
}, {
  id: 'r5',
  title: 'Human Oversight',
  description: 'Meccanismi di supervisione umana implementati',
  status: 'in-progress',
  category: 'oversight'
}, {
  id: 'r6',
  title: 'Transparency & Information',
  description: "Utenti informati dell'interazione con sistema AI",
  status: 'compliant',
  category: 'transparency'
}, {
  id: 'r7',
  title: 'Accuracy, Robustness & Cybersecurity',
  description: 'Misure di sicurezza e robustezza implementate',
  status: 'compliant',
  category: 'security'
}];
const TIMELINE_DATA: TimelineEvent[] = [{
  id: 't1',
  date: '2024-02-01',
  title: 'AI Act pubblicato in Gazzetta Ufficiale UE',
  type: 'milestone',
  status: 'completed'
}, {
  id: 't2',
  date: '2024-08-01',
  title: 'Entrata in vigore divieti sistemi AI vietati',
  type: 'deadline',
  status: 'upcoming'
}, {
  id: 't3',
  date: '2025-08-01',
  title: 'Applicazione regole AI general-purpose',
  type: 'deadline',
  status: 'upcoming'
}, {
  id: 't4',
  date: '2026-08-01',
  title: 'Piena applicazione AI Act',
  type: 'deadline',
  status: 'upcoming'
}];
export function AIActPage() {
  const [activeTab, setActiveTab] = useState<AIActTab>('services');
  const [expandedRequirement, setExpandedRequirement] = useState<string | null>(null);
  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case 'unacceptable':
        return <AppBadge variant="red" data-id="element-1344">Unacceptable</AppBadge>;
      case 'high':
        return <AppBadge variant="amber" data-id="element-1345">High Risk</AppBadge>;
      case 'limited':
        return <AppBadge variant="blue" data-id="element-1346">Limited Risk</AppBadge>;
      case 'minimal':
        return <AppBadge variant="green" data-id="element-1347">Minimal Risk</AppBadge>;
      default:
        return null;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'compliant':
        return <AppBadge variant="green" data-id="element-1348">Conforme</AppBadge>;
      case 'in-progress':
        return <AppBadge variant="amber" data-id="element-1349">In Progress</AppBadge>;
      case 'non-compliant':
        return <AppBadge variant="red" data-id="element-1350">Non Conforme</AppBadge>;
      default:
        return null;
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-[#8eff01]" data-id="element-1351" />;
      case 'in-progress':
        return <Clock className="w-5 h-5 text-amber-400" data-id="element-1352" />;
      case 'non-compliant':
        return <XCircle className="w-5 h-5 text-rose-400" data-id="element-1353" />;
      default:
        return null;
    }
  };
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'risk':
        return <AlertTriangle className="w-4 h-4" data-id="element-1354" />;
      case 'data':
        return <Database className="w-4 h-4" data-id="element-1355" />;
      case 'documentation':
        return <FileText className="w-4 h-4" data-id="element-1356" />;
      case 'oversight':
        return <Users className="w-4 h-4" data-id="element-1357" />;
      case 'transparency':
        return <Eye className="w-4 h-4" data-id="element-1358" />;
      case 'security':
        return <Lock className="w-4 h-4" data-id="element-1359" />;
      default:
        return <Shield className="w-4 h-4" data-id="element-1360" />;
    }
  };
  const overallScore = Math.round(SERVICE_DATA.reduce((sum, s) => sum + s.complianceScore, 0) / SERVICE_DATA.length);
  const totalIssues = SERVICE_DATA.reduce((sum, s) => sum + s.openIssues, 0);
  const highRiskServices = SERVICE_DATA.filter(s => s.riskCategory === 'high').length;
  const compliantRequirements = REQUIREMENTS_DATA.filter(r => r.status === 'compliant').length;
  return <div className="min-h-screen bg-[#121212]" data-id="element-1361">
      {/* Header Section */}
      <div data-id="element-1362">
        <div className="pt-6 px-4 md:px-8 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1363">
          <div data-id="element-1364">
            <h1 className="font-semibold text-white tracking-tight text-[20px]" data-id="element-1365">
              AI Act Compliance
            </h1>
            <p className="text-[13px] text-white/40 mt-1" data-id="element-1366">
              Monitoraggio conformità al Regolamento Europeo sull'Intelligenza
              Artificiale
            </p>
          </div>

          <div className="flex items-center gap-3" data-id="element-1367">
            <PeriodSelector data-id="element-1368" />

            <div className="flex items-center gap-1" data-id="element-1369">
              <button title="Export Report" className="p-2 text-white/70 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] rounded-lg transition-all" data-id="element-1370">
                <Download className="w-4 h-4" data-id="element-1371" />
              </button>
              <button title="Configura Assessment" className="p-2 text-white/70 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] rounded-lg transition-all" data-id="element-1372">
                <Settings className="w-4 h-4" data-id="element-1373" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards Section */}
      <div data-id="element-1374">
        <div className="px-4 md:px-8 py-4" data-id="element-1375">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1376">
            {/* Compliance Score */}
            <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-lg p-3" data-id="element-1377">
              <div className="flex items-center gap-2 mb-2" data-id="element-1378">
                <Shield className="w-4 h-4 text-white/40" data-id="element-1379" />
                <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-1380">
                  Compliance Score
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-1381">
                <p className={`font-bold text-[20px] ${overallScore >= 80 ? 'text-[#8eff01]' : 'text-amber-400'}`} data-id="element-1382">
                  {overallScore}%
                </p>
              </div>
            </div>

            {/* High Risk Services */}
            <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-lg p-3" data-id="element-1383">
              <div className="flex items-center gap-2 mb-2" data-id="element-1384">
                <AlertTriangle className="w-4 h-4 text-white/40" data-id="element-1385" />
                <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-1386">
                  High Risk
                </span>
              </div>
              <p className="font-bold text-white text-[20px]" data-id="element-1387">
                {highRiskServices}
              </p>
            </div>

            {/* Services Monitored */}
            <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-lg p-3" data-id="element-1388">
              <div className="flex items-center gap-2 mb-2" data-id="element-1389">
                <Brain className="w-4 h-4 text-white/40" data-id="element-1390" />
                <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-1391">
                  Servizi
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-1392">
                <p className="font-bold text-white text-[20px]" data-id="element-1393">
                  {SERVICE_DATA.length}
                </p>
              </div>
            </div>

            {/* Open Issues */}
            <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-lg p-3" data-id="element-1394">
              <div className="flex items-center gap-2 mb-2" data-id="element-1395">
                <AlertCircle className="w-4 h-4 text-white/40" data-id="element-1396" />
                <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium" data-id="element-1397">
                  Issues Aperti
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-1398">
                <p className="font-bold text-white text-[20px]" data-id="element-1399">
                  {totalIssues}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="px-4 md:px-8 py-6" data-id="element-1400">
        {/* Risk Classification Overview */}
        <div className="mb-8 bg-white/[0.02] border border-white/[0.08] rounded-lg p-4" data-id="element-1401">
          <h2 className="text-[14px] font-semibold text-white mb-3" data-id="element-1402">
            Distribuzione per Categoria di Rischio
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1403">
            <div className="bg-[#1a1a1a] p-3 rounded-lg border border-white/[0.06]" data-id="element-1404">
              <div className="flex items-center justify-between mb-2" data-id="element-1405">
                <span className="text-[11px] text-white/40 uppercase tracking-wider" data-id="element-1406">
                  Unacceptable
                </span>
                <AppBadge variant="red" data-id="element-1407">0</AppBadge>
              </div>
              <p className="text-[10px] text-white/30" data-id="element-1408">Sistemi vietati</p>
            </div>
            <div className="bg-[#1a1a1a] p-3 rounded-lg border border-white/[0.06]" data-id="element-1409">
              <div className="flex items-center justify-between mb-2" data-id="element-1410">
                <span className="text-[11px] text-white/40 uppercase tracking-wider" data-id="element-1411">
                  High Risk
                </span>
                <AppBadge variant="amber" data-id="element-1412">{highRiskServices}</AppBadge>
              </div>
              <p className="text-[10px] text-white/30" data-id="element-1413">Conformità rigorosa</p>
            </div>
            <div className="bg-[#1a1a1a] p-3 rounded-lg border border-white/[0.06]" data-id="element-1414">
              <div className="flex items-center justify-between mb-2" data-id="element-1415">
                <span className="text-[11px] text-white/40 uppercase tracking-wider" data-id="element-1416">
                  Limited Risk
                </span>
                <AppBadge variant="blue" data-id="element-1417">
                  {SERVICE_DATA.filter(s => s.riskCategory === 'limited').length}
                </AppBadge>
              </div>
              <p className="text-[10px] text-white/30" data-id="element-1418">Trasparenza</p>
            </div>
            <div className="bg-[#1a1a1a] p-3 rounded-lg border border-white/[0.06]" data-id="element-1419">
              <div className="flex items-center justify-between mb-2" data-id="element-1420">
                <span className="text-[11px] text-white/40 uppercase tracking-wider" data-id="element-1421">
                  Minimal Risk
                </span>
                <AppBadge variant="green" data-id="element-1422">
                  {SERVICE_DATA.filter(s => s.riskCategory === 'minimal').length}
                </AppBadge>
              </div>
              <p className="text-[10px] text-white/30" data-id="element-1423">Nessun obbligo</p>
            </div>
          </div>
        </div>

        {/* Section Header with Tabs */}
        <div className="mb-5" data-id="element-1424">
          <div className="flex items-center gap-1 border-b border-white/[0.06]" data-id="element-1425">
            <button onClick={() => setActiveTab('services')} className={`px-4 py-3 text-[13px] font-medium transition-all relative ${activeTab === 'services' ? 'text-white' : 'text-white/35 hover:text-white/60'}`} data-id="element-1426">
              Servizi
              {activeTab === 'services' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-1427" />}
            </button>
            <button onClick={() => setActiveTab('requirements')} className={`px-4 py-3 text-[13px] font-medium transition-all relative ${activeTab === 'requirements' ? 'text-white' : 'text-white/35 hover:text-white/60'}`} data-id="element-1428">
              Requisiti
              {activeTab === 'requirements' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-1429" />}
            </button>
            <button onClick={() => setActiveTab('timeline')} className={`px-4 py-3 text-[13px] font-medium transition-all relative ${activeTab === 'timeline' ? 'text-white' : 'text-white/35 hover:text-white/60'}`} data-id="element-1430">
              Timeline
              {activeTab === 'timeline' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-1431" />}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-[#1a1a1a] border border-white/[0.08] rounded-lg overflow-hidden" data-id="element-1432">
          {activeTab === 'services' && <div className="overflow-x-auto" data-id="element-1433">
              <table className="w-full" data-id="element-1434">
                <thead className="bg-white/[0.02] border-b border-white/[0.06]" data-id="element-1435">
                  <tr data-id="element-1436">
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1437">
                      Servizio
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1438">
                      Categoria Rischio
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1439">
                      Compliance
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1440">
                      Issues
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1441">
                      Ultimo Assessment
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-medium text-white/30 uppercase tracking-wider" data-id="element-1442">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]" data-id="element-1443">
                  {SERVICE_DATA.map(service => <tr key={service.id} className="hover:bg-white/[0.02] transition-colors" data-id="element-1444">
                      <td className="px-4 py-4" data-id="element-1445">
                        <div data-id="element-1446">
                          <p className="text-[13px] font-medium text-white" data-id="element-1447">
                            {service.name}
                          </p>
                          <p className="text-[11px] text-white/40" data-id="element-1448">
                            {service.provider}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-4" data-id="element-1449">
                        {getRiskBadge(service.riskCategory)}
                      </td>
                      <td className="px-4 py-4" data-id="element-1450">
                        <div className="flex items-center gap-2" data-id="element-1451">
                          <span className={`text-[13px] font-semibold ${service.complianceScore >= 80 ? 'text-[#8eff01]' : 'text-amber-400'}`} data-id="element-1452">
                            {service.complianceScore}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4" data-id="element-1453">
                        {service.openIssues > 0 ? <span className="text-[13px] text-amber-400 font-medium" data-id="element-1454">
                            {service.openIssues}
                          </span> : <span className="text-[13px] text-white/40" data-id="element-1455">0</span>}
                      </td>
                      <td className="px-4 py-4" data-id="element-1456">
                        <span className="text-[12px] text-white/50" data-id="element-1457">
                          {new Date(service.lastAssessment).toLocaleDateString('it-IT')}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right" data-id="element-1458">
                        <button className="text-[12px] text-[#8eff01] hover:text-[#8eff01]/80 font-medium" data-id="element-1459">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}

          {activeTab === 'requirements' && <div className="p-4 space-y-3" data-id="element-1460">
              {REQUIREMENTS_DATA.map(req => <div key={req.id} className="bg-white/[0.03] border border-white/[0.08] rounded-lg overflow-hidden" data-id="element-1461">
                  <button onClick={() => setExpandedRequirement(expandedRequirement === req.id ? null : req.id)} className="w-full flex items-start gap-3 p-4 hover:bg-white/[0.02] transition-colors" data-id="element-1462">
                    {getStatusIcon(req.status)}
                    <div className="flex-1 text-left" data-id="element-1463">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1464">
                        <h3 className="text-[13px] font-medium text-white" data-id="element-1465">
                          {req.title}
                        </h3>
                        {getStatusBadge(req.status)}
                      </div>
                      <p className="text-[11px] text-white/50" data-id="element-1466">
                        {req.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1467">
                      <div className="p-1.5 bg-white/[0.05] rounded-lg text-white/40" data-id="element-1468">
                        {getCategoryIcon(req.category)}
                      </div>
                      {expandedRequirement === req.id ? <ChevronDown className="w-4 h-4 text-white/30" data-id="element-1469" /> : <ChevronRight className="w-4 h-4 text-white/30" data-id="element-1470" />}
                    </div>
                  </button>
                  {expandedRequirement === req.id && <div className="px-4 pb-4 border-t border-white/[0.06] pt-3" data-id="element-1471">
                      <p className="text-[12px] text-white/60 leading-relaxed" data-id="element-1472">
                        Dettagli aggiuntivi e azioni correttive per questo
                        requisito verranno visualizzati qui.
                      </p>
                    </div>}
                </div>)}
            </div>}

          {activeTab === 'timeline' && <div className="p-4" data-id="element-1473">
              <div className="space-y-4" data-id="element-1474">
                {TIMELINE_DATA.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-1475">
                    <div className="flex flex-col items-center" data-id="element-1476">
                      <div className={`w-3 h-3 rounded-full ${event.status === 'completed' ? 'bg-[#8eff01]' : event.status === 'overdue' ? 'bg-rose-400' : 'bg-white/20'}`} data-id="element-1477" />
                      {index < TIMELINE_DATA.length - 1 && <div className="w-px h-full bg-white/[0.10] mt-2" data-id="element-1478" />}
                    </div>
                    <div className="flex-1 pb-6" data-id="element-1479">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1480">
                        <span className="text-[11px] text-white/50" data-id="element-1481">
                          {new Date(event.date).toLocaleDateString('it-IT', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                        </span>
                        {event.type === 'deadline' && <AppBadge variant="amber" data-id="element-1482">Deadline</AppBadge>}
                        {event.type === 'milestone' && <AppBadge variant="blue" data-id="element-1483">Milestone</AppBadge>}
                      </div>
                      <p className="text-[13px] text-white/90 font-medium" data-id="element-1484">
                        {event.title}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}